/**
 * @file This file contains the configuration for all UI prompts in the game.
 * @module promptsConfig
 */

import {
    GAME_TYPE_QUICK_CLICK,
    GAME_TYPE_CAMPAIGN_CLICK,
    GAME_TYPE_CAMPAIGN_ROLLING
} from '../constants/gameTypes.js';

/**
 * Generates the configuration object for all prompts.
 * @param {object} [detail={}] - The detail object, often containing save slot data.
 * @returns {object} The complete prompts configuration object.
 */
export const getPromptsConfig = (detail = {}) => ({
    newGamePrompt: {
        title: 'Start New Quick Game?',
        message: 'This will delete your current saved quick game. Are you sure?',
        buttons: [
            { text: 'Yes, Start New', event: 'prompt:startNewQuickGame' },
            { text: 'No, Continue my saved game', event: 'prompt:loadGameByType', detail: { type: GAME_TYPE_QUICK_CLICK, savedSlot: detail.quickSave } },
            { text: 'Cancel', event: 'prompt:cancel' }
        ]
    },
    continuePrompt: {
        title: 'Continue Game?',
        message: 'Would you like to continue your saved quick game?',
        buttons: [
            { text: 'Yes, Continue', event: 'prompt:loadGameByType', detail: { type: GAME_TYPE_QUICK_CLICK, savedSlot: detail.quickSave } },
            { text: 'No, Start New', event: 'prompt:startNewQuickGame' }
        ]
    },
    campaignPrompt: {
        title: 'Continue Campaign?',
        message: 'Would you like to continue your saved campaign?',
        buttons: [
            { text: 'Yes, Continue', event: 'prompt:loadGameByType', detail: { type: GAME_TYPE_CAMPAIGN_CLICK, savedSlot: detail.campaignSave } },
            { text: 'No, Start New', event: 'prompt:startNewCampaign' }
        ]
    },
    newCampaignPrompt: {
        title: 'Start New Campaign?',
        message: 'This will delete your current saved campaign progress. Are you sure?',
        buttons: [
            { text: 'Yes, Start New', event: 'prompt:startNewCampaign' },
            { text: 'No, Continue my saved campaign', event: 'prompt:loadGameByType', detail: { type: GAME_TYPE_CAMPAIGN_CLICK, savedSlot: detail.campaignSave } },
            { text: 'Cancel', event: 'prompt:cancel' }
        ]
    },
    newRollingCampaignPrompt: {
        title: 'Start New Rolling Campaign?',
        message: 'This will delete your current saved rolling campaign progress. Are you sure?',
        buttons: [
            { text: 'Yes, Start New', event: 'prompt:startNewRollingGame' },
            { text: 'No, Continue my saved game', event: 'prompt:loadGameByType', detail: { type: GAME_TYPE_CAMPAIGN_ROLLING, savedSlot: detail.rollingCampaignSave } },
            { text: 'Cancel', event: 'prompt:cancel' }
        ]
    },
    multiSavePrompt: {
        title: 'Continue Game?',
        message: 'You have both a Quick Game and a Campaign save. Which would you like to continue?',
        buttons: [
            { text: 'Continue Quick Game', event: 'prompt:loadGameByType', detail: { type: GAME_TYPE_QUICK_CLICK, savedSlot: detail.quickSave } },
            { text: 'Continue Campaign', event: 'prompt:loadGameByType', detail: { type: GAME_TYPE_CAMPAIGN_CLICK, savedSlot: detail.campaignSave } },
            { text: 'Cancel', event: 'prompt:cancel' }
        ]
    },
    resetProfileConfirm: {
        title: 'Reset Profile?',
        message: 'Are you sure? This will permanently delete all your achievements and gold. This cannot be undone.',
        buttons: [
            { text: 'Yes, Reset Everything', event: 'prompt:resetProfileConfirmed' },
            { text: 'Cancel', event: 'prompt:cancel' }
        ]
    },
    newGameHub: {
        title: 'Start a New Game',
        message: 'Choose a game mode to begin.',
        buttons: [
            { text: 'Quick Game', event: 'prompt:newGameHub:quick' },
            { text: 'Campaign', event: 'prompt:newGameHub:campaign' },
            { text: 'Rolling Mode', event: 'prompt:newGameHub:rolling' },
            { text: 'Mini Merge', event: 'prompt:newGameHub:miniMerge' },
            { text: 'Back', event: 'prompt:cancel' }
        ],
        targetPage: 'newGameHub'
    },
    autosavePrompt: {
        title: 'Autosave',
        message: 'Your game has been autosaved. Do you want to continue?',
        buttons: [
            { text: 'Yes', event: 'prompt:autosave:continue' },
            { text: 'No', event: 'prompt:autosave:exit' }
        ]
    }
});